import {
    mdiArrowDownLeft,
    mdiArrowUpRight,
    mdiCheckCircleOutline,
    mdiClose,
    mdiMinus,
    mdiOfficeBuildingOutline,
    mdiShareVariantOutline,
    mdiSwapHorizontal,
} from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React from 'react';
import {
    bindingTransactionLabels,
    countBindingTransactions,
    countCovered,
    countRouted,
    DATA_KIND_ICON,
    DATA_KIND_PATH_LABEL,
    DATA_KINDS,
    getPaperPath,
    getPayerLanes,
    getTransactionCoverage,
    IRoutableConnection,
    IRoutablePayer,
    PayerLaneDirection,
} from '../../services/payer-routing';
import { Variables } from '../base/variables';
import { BaseButton } from '../base/button/button';
import { PayerIdChip } from '../payer-id-chip/payer-id-chip';
import { PathChip } from '../payer-path/payer-path';
import { PayerTransactionTable } from './payer-transaction-table';
import * as S from './payer-drawer.styles';

/**
 * Everything known about one payer's routing, in a drawer.
 *
 * ONE component, rendered by both the support console and the provider app. It used to be two
 * files that had already begun to drift — different loading text, different empty states,
 * different arrow icons, and fourteen styled components whose padding and type sizes no longer
 * matched. A payer routing change landing in one copy and not the other is the failure this
 * prevents.
 *
 * ## It takes structural types, not app models
 *
 * `SupportPayer` and `ProviderPayer` are structurally identical here and nominally distinct,
 * which is the only reason there were ever two of these. `IRoutablePayer` and
 * `IRoutableConnection` already describe exactly the fields routing reads, and the four
 * transaction helpers were widened to match, so neither app model appears in this file.
 *
 * ## The host supplies its own EMR tags and its own actions
 *
 * `emrTags` is passed in rather than read off the payer, because "which tags are EMR payers"
 * is an app-level question: support must filter to `TagType.INTEGRATION`, and the provider tag
 * type has exactly one member so every tag qualifies. Putting that filter in here would encode
 * one app's rule for both.
 *
 * `actions` is a NODE, not a set of callbacks — deliberately. Support's drawer carries `Edit
 * payer`, `Map EMR payer` and `Halt payer`; the provider app is read-only and must never show
 * any of them (@John Russell, 2026-08-25). With optional callbacks, "provider passes none" is
 * the only thing standing between a read-only app and three write controls, and a future
 * default or a copy-paste breaks it silently. A node cannot be rendered unless the caller
 * constructed it.
 *
 * ## Loading is not "nothing is routed"
 *
 * `bindings === undefined` means the clearinghouse join has not resolved. Every section that
 * depends on it renders a distinct loading state rather than its empty state — telling someone
 * that nothing is routed because a request is in flight is a false statement about where real
 * claims go (NS-3). `getTransactionCoverage` in particular returns every transaction UNSERVED
 * for undefined bindings, so its table must say so rather than read as "this clearinghouse
 * supports nothing".
 */

/** Which way the data moves. Remittance is the only inbound lane (NS-7). */
const DIRECTION_ICON: Record<PayerLaneDirection, string> = {
    Outbound: mdiArrowUpRight,
    Inbound: mdiArrowDownLeft,
    'Two-way': mdiSwapHorizontal,
};

function Head({ title, meta }: { title: string; meta?: string }): React.ReactElement {
    return (
        <S.SectionHead>
            <S.SectionTitle>{title}</S.SectionTitle>
            {meta && <S.SectionMeta>{meta}</S.SectionMeta>}
        </S.SectionHead>
    );
}

/** The payer fields the drawer renders, on top of the ones routing already reads. */
export interface IDrawerPayer extends IRoutablePayer {
    /** The ENTER payer ID — the GUID. Always present, and what operators paste elsewhere. */
    id?: string;
    coverage?: string;
    halt?: boolean;
}

/** An EMR payer identifier mapped into this payer. Both apps' tag models satisfy this. */
export interface IDrawerEmrTag {
    id?: string;
    name?: string;
}

export interface IPayerDrawerProps {
    payer: IDrawerPayer;
    /** `undefined` while the clearinghouse join is in flight — NOT "no connections". */
    bindings: IRoutableConnection[] | undefined;
    /** Already filtered by the host: support excludes non-INTEGRATION tags, provider cannot have any. */
    emrTags: IDrawerEmrTag[];
    /** Small label above the title. Support says "ENTER payer"; the provider app says "Payer". */
    eyebrow?: string;
    /** Write controls, if this app has any. The provider app passes none, and must not. */
    actions?: React.ReactNode;
    onClose: () => void;
}

export const PayerDrawer: React.FunctionComponent<IPayerDrawerProps> = observer(
    ({ payer, bindings, emrTags, eyebrow = 'Payer', actions, onClose }: IPayerDrawerProps): React.ReactElement => {
        const loading = bindings === undefined;
        const connections = bindings || [];
        const lanes = getPayerLanes(payer, bindings);
        const routed = countRouted(lanes);
        const coverage = getTransactionCoverage(bindings);
        const covered = countCovered(coverage);
        const paper = DATA_KINDS.map(kind => ({ kind, path: getPaperPath(payer.contact, kind) }));
        const paperSet = paper.filter(p => p.path).length;

        return (
            <S.Body>
                <S.Header>
                    <S.TitleRow>
                        <div>
                            <S.Eyebrow>{eyebrow}</S.Eyebrow>
                            <S.Title>{payer.name}</S.Title>
                        </div>
                        <BaseButton variant="text" size="small" aria-label="Close detail" onClick={onClose}>
                            <Icon path={mdiClose} size={0.8} />
                        </BaseButton>
                    </S.TitleRow>

                    <S.Chips>
                        {/* No chip at all while loading: "Paper only" computed from a join that
                            has not landed is the exact wrong answer to show first. */}
                        {!loading && (
                            <S.Chip>
                                <Icon path={mdiShareVariantOutline} size={0.5} />
                                {connections.length ? 'Clearinghouse' : 'Paper only'}
                            </S.Chip>
                        )}
                        {connections.map(b => (
                            <S.Chip key={b.externalId || b.payer?.name}>{`EDI ${b.externalId || '—'}`}</S.Chip>
                        ))}
                        {payer.coverage && <S.Chip>{String(payer.coverage).replace(/_/g, ' ').toLowerCase()}</S.Chip>}
                        {/* Halt as a chip, not only as a button label. An app with no Halt
                            control still has to be able to say that claims are not going out. */}
                        {payer.halt && <S.Chip>HALTED</S.Chip>}
                        {/* The GUID, copyable. @John Russell, 2026-08-30: "We want to make sure
                            GUIDs are always available to the user somewhere." */}
                        <PayerIdChip id={payer.id} />
                    </S.Chips>

                    {actions && <S.Actions>{actions}</S.Actions>}
                </S.Header>

                <S.Scroll>
                    {/* ── CHAIN ─────────────────────────────────────────────── */}
                    <S.Section>
                        <Head title="Chain" />
                        <S.Card>
                            <S.Row>
                                <S.Icon>
                                    <Icon path={mdiOfficeBuildingOutline} size={0.65} />
                                </S.Icon>
                                <S.Text>
                                    <S.Muted>From the EMR</S.Muted>
                                    <S.Strong>{`${emrTags.length} EMR payer${emrTags.length === 1 ? '' : 's'}`}</S.Strong>
                                    {emrTags.length > 0 && (
                                        <S.Muted title={emrTags.map(t => t.name).join(' · ')}>
                                            {emrTags.map(t => t.name).join(' · ')}
                                        </S.Muted>
                                    )}
                                </S.Text>
                            </S.Row>
                        </S.Card>
                        <S.Card>
                            <S.Row>
                                <S.Text>
                                    <S.Muted>ENTER payer</S.Muted>
                                    <S.Strong>{payer.name}</S.Strong>
                                </S.Text>
                            </S.Row>
                        </S.Card>
                        <S.Card>
                            <S.Row>
                                <S.Icon>
                                    <Icon path={mdiShareVariantOutline} size={0.65} />
                                </S.Icon>
                                <S.Text>
                                    <S.Muted>
                                        {loading
                                            ? 'Clearinghouse payers'
                                            : `${connections.length} clearinghouse payer${
                                                  connections.length === 1 ? '' : 's'
                                              }`}
                                    </S.Muted>
                                    <S.Strong>
                                        {loading
                                            ? 'Loading…'
                                            : connections.length
                                              ? connections
                                                    .map(b => b.payer?.name || b.clearinghouse?.name || 'Connection')
                                                    .join(' · ')
                                              : 'None — everything drops to paper'}
                                    </S.Strong>
                                    {!loading && connections.length > 0 && (
                                        <S.Muted>{`${covered.covered} of ${covered.total} transactions covered`}</S.Muted>
                                    )}
                                </S.Text>
                            </S.Row>
                        </S.Card>
                    </S.Section>

                    {/* ── WHERE EACH KIND OF DATA GOES ──────────────────────── */}
                    <S.Section>
                        <Head
                            title="Where each kind of data goes"
                            meta={loading ? 'checking connections…' : `${routed.routed} of ${routed.total} routed`}
                        />
                        <S.Table>
                            {lanes.map(lane => (
                                <S.Lane key={lane.id}>
                                    <S.Icon>
                                        <Icon path={DIRECTION_ICON[lane.direction]} size={0.6} />
                                    </S.Icon>
                                    <S.Text>
                                        <S.Strong>{lane.label}</S.Strong>
                                        {lane.destination && (
                                            <S.Muted title={lane.destination}>{lane.destination}</S.Muted>
                                        )}
                                        <S.Muted title={lane.detail}>{lane.detail}</S.Muted>
                                    </S.Text>
                                    <PathChip path={lane.path} />
                                </S.Lane>
                            ))}
                        </S.Table>
                    </S.Section>

                    {/* ── CLEARINGHOUSE PAYERS ──────────────────────────────── */}
                    <S.Section>
                        <Head
                            title="Clearinghouse payers"
                            meta={
                                loading
                                    ? undefined
                                    : `${connections.length} connection${connections.length === 1 ? '' : 's'} · ${
                                          covered.covered
                                      } of ${covered.total} covered`
                            }
                        />
                        {loading ? (
                            <S.Empty>Loading clearinghouse connections…</S.Empty>
                        ) : connections.length === 0 ? (
                            <S.Empty>No clearinghouse connection — everything drops to paper.</S.Empty>
                        ) : (
                            connections.map(binding => {
                                const own = countBindingTransactions(binding);

                                return (
                                    <S.Card key={binding.externalId || binding.payer?.name}>
                                        <S.Row>
                                            <S.Icon>
                                                <Icon path={mdiShareVariantOutline} size={0.65} />
                                            </S.Icon>
                                            <S.Text>
                                                <S.Strong>
                                                    {`${binding.externalId || '—'} — ${
                                                        binding.payer?.name ||
                                                        binding.clearinghouse?.name ||
                                                        'Connection'
                                                    }`}
                                                </S.Strong>
                                                <S.Muted>
                                                    {`${binding.clearinghouse?.name || 'Clearinghouse'} · ${
                                                        own.covered
                                                    } of ${own.total} transactions`}
                                                </S.Muted>
                                            </S.Text>
                                        </S.Row>
                                        <S.Handles>
                                            {bindingTransactionLabels(binding).map(label => (
                                                <S.Handle key={label}>
                                                    <Icon path={mdiCheckCircleOutline} size={0.45} />
                                                    {label}
                                                </S.Handle>
                                            ))}
                                        </S.Handles>
                                    </S.Card>
                                );
                            })
                        )}
                    </S.Section>

                    {/* ── TRANSACTION / SERVED BY ───────────────────────────── */}
                    <S.Section>
                        <Head title="Transaction coverage" />
                        <PayerTransactionTable coverage={coverage} loading={loading} />
                    </S.Section>

                    {/* ── EMR PAYERS MAPPED IN ──────────────────────────────── */}
                    <S.Section>
                        <Head title={`EMR payers mapped in (${emrTags.length})`} />
                        {emrTags.length === 0 ? (
                            <S.Empty>No EMR payer maps into this payer yet.</S.Empty>
                        ) : (
                            emrTags.map(tag => (
                                <S.Card key={tag.id || tag.name}>
                                    <S.Row>
                                        <S.Icon>
                                            <Icon path={mdiOfficeBuildingOutline} size={0.65} />
                                        </S.Icon>
                                        <S.Text>
                                            {/* The design shows the originating system underneath
                                                ("ModMed · payer 3102"). The tag carries only a
                                                name — confirmed absent by the backend owner — so
                                                the identifier stands alone rather than being
                                                invented (NS-4). */}
                                            <S.Strong title={tag.name}>{tag.name}</S.Strong>
                                        </S.Text>
                                    </S.Row>
                                </S.Card>
                            ))
                        )}
                    </S.Section>

                    {/* ── DROP-TO-PAPER PATHS ───────────────────────────────── */}
                    <S.Section>
                        <Head title="Drop-to-paper paths" meta={`${paperSet} of ${paper.length} set`} />
                        {paper.map(({ kind, path }) => (
                            <S.Card key={kind}>
                                <S.Row>
                                    <S.Icon>
                                        <Icon path={DATA_KIND_ICON[kind]} size={0.65} />
                                    </S.Icon>
                                    <S.Text>
                                        <S.Strong>{DATA_KIND_PATH_LABEL[kind]}</S.Strong>
                                        <S.Muted title={path?.destination}>
                                            {path ? path.destination : 'Not configured'}
                                        </S.Muted>
                                    </S.Text>
                                    {path ? <PathChip path={path.path} /> : <S.Muted>None</S.Muted>}
                                </S.Row>
                            </S.Card>
                        ))}
                    </S.Section>
                </S.Scroll>
            </S.Body>
        );
    },
);

export const PayerDrawerPanel = S.Panel;
